import { useMemo, useCallback } from "react";
import { useController } from "react-hook-form";
import type { ChangeEvent } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";

const COUNTRY_CODES = [
  { code: "+1", label: "US/CA (+1)" },
  { code: "+44", label: "UK (+44)" },
  { code: "+55", label: "BR (+55)" },
  { code: "+52", label: "MX (+52)" },
  { code: "+33", label: "FR (+33)" },
  { code: "+49", label: "DE (+49)" },
  { code: "+34", label: "ES (+34)" },
  { code: "+39", label: "IT (+39)" },
  { code: "+61", label: "AU (+61)" },
  { code: "+81", label: "JP (+81)" },
];

interface PhoneInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  disabled?: boolean;
  placeholder?: string;
}

export function PhoneInput<T extends FieldValues>({
  control,
  name,
  disabled,
  placeholder = "Phone number...",
}: PhoneInputProps<T>) {
  const { field } = useController({ control, name });

  const { countryCode, numberOnly } = useMemo(() => {
    const stringValue = String(field.value ?? "");

    const sortedCodes = [...COUNTRY_CODES].sort(
      (a, b) => b.code.length - a.code.length,
    );

    const match = sortedCodes.find((c) => stringValue.startsWith(c.code));

    if (match) {
      return {
        countryCode: match.code,
        numberOnly: stringValue.slice(match.code.length).trim(),
      };
    }

    return { countryCode: "+1", numberOnly: stringValue };
  }, [field.value]);

  const inputRef = useCallback(
    (el: HTMLInputElement | null) => {
      if (typeof field.ref === "function") field.ref(el);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value.replace(/\D/g, "");
    field.onChange(newNumber ? `${countryCode} ${newNumber}` : "");
  };

  const handleCodeChange = (newCode: string) => {
    field.onChange(`${newCode} ${numberOnly}`);
  };

  return (
    <div
      className="flex items-center w-full overflow-hidden rounded-[6px] border border-(--gray-100) bg-(--brand-white)"
      style={{ height: 40 }}
    >
      <Select value={countryCode} onValueChange={handleCodeChange} disabled={disabled}>
        <SelectTrigger
          className="h-full rounded-none border-0 shadow-none focus:ring-0 focus:ring-offset-0 bg-transparent shrink-0 pl-3 pr-2 gap-1 text-sm font-normal w-auto text-(--brand-black)"
        >
          <span>{countryCode}</span>
        </SelectTrigger>
        <SelectContent>
          {COUNTRY_CODES.map(({ code, label }) => (
            <SelectItem key={code} value={code}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="w-px self-stretch my-2 shrink-0 bg-(--gray-100)" />

      <input
        ref={inputRef}
        type="tel"
        value={numberOnly}
        onChange={handleNumberChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        inputMode="numeric"
        pattern="[0-9]*"
        className="flex-1 h-full px-3 text-sm bg-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed text-(--brand-black)"
      />
    </div>
  );
}